import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePropertyPlmContext } from '../../properties/PropertyContextWrapper';

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  hasTasks: boolean;
  tasks: Array<{
    id: string;
    start: Date;
    end: Date;
    title: string;
    isCurrent: boolean;
    deviceName: string;
  }>;
}

export function UnitPlmCalendar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('propertyId');
  const unitId = searchParams.get('unitId');
  const returnTo = searchParams.get('returnTo') || '/properties';

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);

  const { unitPlmTasks } = usePropertyPlmContext();

  const unitTasks = unitPlmTasks[unitId || ''];
  const allTasks = useMemo(
    () =>
      unitTasks ? [...unitTasks.currentTasks, ...unitTasks.scheduledTasks] : [],
    [unitTasks],
  );

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: CalendarDay[] = [];
    const currentDateForLoop = new Date(startDate);

    while (currentDateForLoop <= lastDay || days.length < 42) {
      const dayDate = new Date(currentDateForLoop);
      const isCurrentMonth = dayDate.getMonth() === month;

      // Find tasks for this day
      const dayTasks = allTasks
        .filter((task) => {
          const taskDate = new Date(task.scheduledStart);
          return taskDate.toDateString() === dayDate.toDateString();
        })
        .map((task) => ({
          id: task._id,
          start: new Date(task.scheduledStart),
          end: new Date(task.scheduledEnd),
          title: `PLM Task - ${task.deviceId}`,
          isCurrent:
            unitTasks?.currentTasks.some((t) => t._id === task._id) || false,
          deviceName: task.deviceId,
        }));

      days.push({
        date: dayDate,
        day: dayDate.getDate(),
        isCurrentMonth,
        hasTasks: dayTasks.length > 0,
        tasks: dayTasks,
      });

      currentDateForLoop.setDate(currentDateForLoop.getDate() + 1);
    }

    return days;
  }, [currentDate, allTasks, unitTasks]);

  if (!propertyId || !unitId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" color="error">
          Property ID and Unit ID are required
        </Typography>
      </Box>
    );
  }

  const monthName = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const handleDayClick = (day: CalendarDay) => {
    if (day.hasTasks) {
      setSelectedDay(day);
      setShowTaskDialog(true);
    }
  };

  const handleTaskClick = (taskId: string) => {
    setShowTaskDialog(false);
    // Find the task to get the response ID
    const task = allTasks.find((t) => t._id === taskId);
    if (task) {
      navigate(
        `/plm/event-responses/${task.plmEventResponseId}/timeline?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`,
      );
    }
  };

  const handleCloseDialog = () => {
    setShowTaskDialog(false);
    setSelectedDay(null);
  };

  const handleBackToUnits = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(`/properties/${propertyId}/installer`);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={handleBackToUnits}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Unit PLM Event Calendar
        </Typography>
      </Box>

      {/* Calendar Navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <IconButton onClick={handlePreviousMonth}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">{monthName}</Typography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForward />
          </IconButton>
        </Box>
      </Paper>

      {/* Calendar Grid */}
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid item xs key={day}>
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {day}
                </Typography>
              </Box>
            </Grid>
          ))}

          {/* Calendar Days */}
          {calendarDays.map((day, index) => (
            <Grid item xs key={index}>
              <Box
                sx={{
                  p: 1,
                  height: 80,
                  border: '1px solid #e0e0e0',
                  cursor: day.hasTasks ? 'pointer' : 'default',
                  backgroundColor: day.isCurrentMonth ? 'white' : '#f5f5f5',
                  '&:hover': day.hasTasks
                    ? {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#2196f3',
                      }
                    : {},
                }}
                onClick={() => handleDayClick(day)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: day.isCurrentMonth
                      ? 'text.primary'
                      : 'text.disabled',
                    fontWeight: day.hasTasks ? 'bold' : 'normal',
                  }}
                >
                  {day.day}
                </Typography>
                {day.hasTasks && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={`${day.tasks.length} task${day.tasks.length > 1 ? 's' : ''}`}
                      size="small"
                      color={
                        day.tasks.some((t) => t.isCurrent)
                          ? 'success'
                          : 'primary'
                      }
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Task Details Dialog */}
      <Dialog
        open={showTaskDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Tasks for{' '}
          {selectedDay?.date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </DialogTitle>
        <DialogContent>
          {selectedDay?.tasks.map((task, index) => (
            <ListItem
              key={task.id}
              button
              onClick={() => handleTaskClick(task.id)}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                backgroundColor: task.isCurrent
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: task.isCurrent
                    ? 'rgba(76, 175, 80, 0.2)'
                    : '#f5f5f5',
                },
              }}
            >
              <ListItemText
                primary={task.title}
                secondary={`${task.start.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })} - ${task.end.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}`}
              />
              {task.isCurrent && (
                <Chip
                  label="Active"
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              )}
            </ListItem>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
